import { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import DeclassementBadge from "@/components/DeclassementAlertBadge";
import { fmtDate, batimentColor } from "@/lib/helpers";
import { Boxes, PlusCircle, Truck, TrendingUp } from "lucide-react";

export default function Dashboard() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data));
  }, []);

  if (!data)
    return <div className="animate-pulse text-slate-400 font-heading uppercase tracking-widest">Chargement…</div>;

  return (
    <div className="space-y-6" data-testid="dashboard">
      <div>
        <h1 className="font-heading font-black text-3xl sm:text-4xl uppercase tracking-tight text-slate-900">
          Poste de Contrôle
        </h1>
        <p className="text-slate-500 font-medium">Quai & stock en temps réel</p>
      </div>

      {/* Big CTA */}
      <button
        data-testid="dashboard-cta-new-palette"
        onClick={() => navigate("/nouvelle-palette")}
        className="w-full min-h-[80px] rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white flex items-center justify-center gap-3 font-heading font-black text-2xl uppercase tracking-wide active:scale-[0.99] transition-transform shadow-lg shadow-emerald-700/20"
      >
        <PlusCircle className="w-9 h-9" /> Saisir une nouvelle palette
      </button>

      {/* Stock détaillé par bâtiment */}
      <div className="rounded-2xl bg-white border-2 border-slate-200 overflow-hidden" data-testid="stock-detail-table">
        <div className="p-5 pb-3 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="font-heading font-bold text-xl uppercase tracking-tight text-slate-900 flex items-center gap-2">
              <Boxes className="w-5 h-5" /> Stock en attente d'expédition
            </h2>
            <p className="text-xs text-slate-400 font-semibold">Palettes en stock par bâtiment — nombre et poids net</p>
          </div>
          <div className="flex gap-3 font-mono">
            <div data-testid="stat-stock-conforme" className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-1.5">
              <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">Total conforme</div>
              <div className="font-black text-xl text-emerald-800">{data.stock_total.P.nb} <span className="text-xs font-bold text-emerald-600">pal · {data.stock_total.P.net.toFixed(1)} kg</span></div>
            </div>
            <div data-testid="stat-stock-declassee" className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-1.5">
              <div className="text-[10px] uppercase tracking-wider text-rose-700 font-bold">Total déclassé</div>
              <div className="font-black text-xl text-rose-800">{data.stock_total.D.nb} <span className="text-xs font-bold text-rose-600">pal · {data.stock_total.D.net.toFixed(1)} kg</span></div>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-y-2 border-slate-200">
              <tr className="text-xs uppercase tracking-wide text-slate-500 font-bold">
                {Object.keys(data.stock_detail).map((b) => (
                  <Fragment key={b}>
                    <th className="px-3 py-2 text-center">{b}</th>
                    <th className="px-3 py-2 text-center bg-rose-50 text-rose-700">{b} Déclassé</th>
                  </Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                {Object.entries(data.stock_detail).map(([b, cell]) => (
                  <Fragment key={b}>
                    <td data-testid={`stock-${b}-conforme`} className="px-3 py-4 text-center">
                      <div className="font-mono font-black text-3xl text-slate-900">{cell.P.nb}</div>
                      <div className="text-xs font-mono font-bold text-slate-500">{cell.P.net.toFixed(1)} kg</div>
                    </td>
                    <td data-testid={`stock-${b}-declassee`} className="px-3 py-4 text-center bg-rose-50/60">
                      <div className="font-mono font-black text-3xl text-rose-700">{cell.D.nb}</div>
                      <div className="text-xs font-mono font-bold text-rose-500">{cell.D.net.toFixed(1)} kg</div>
                    </td>
                  </Fragment>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Stat grid */}
      <div className="grid grid-cols-2 gap-4">
        <StatCard testId="stat-prod-jour" icon={TrendingUp} label="Production du jour"
          value={data.total_jour} sub="Palettes saisies aujourd'hui" tone="slate" />
        <div data-testid="stat-taux-declassement" className="rounded-2xl bg-white border-2 border-slate-200 p-5">
          <div className="text-xs uppercase tracking-wider text-slate-400 font-bold mb-2">
            Taux déclassement (jour)
          </div>
          <DeclassementBadge taux={data.taux_declassement} seuil={data.seuil} testId="alert-declassement-banner" />
          <div className="text-xs text-slate-400 mt-2 font-semibold">Seuil d'alerte {data.seuil}%</div>
        </div>
      </div>

      {/* Production by building */}
      <div className="rounded-2xl bg-white border-2 border-slate-200 p-5">
        <h2 className="font-heading font-bold text-xl uppercase tracking-tight text-slate-900 mb-4">
          Production du jour par bâtiment
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {Object.entries(data.production_jour).map(([bat, p]) => (
            <div key={bat} className="rounded-xl border-2 border-slate-200 p-4">
              <div className={`inline-block px-3 py-1 rounded-md font-heading font-black text-lg ${batimentColor(bat)}`}>
                {bat}
              </div>
              <div className="mt-3 flex justify-between text-sm">
                <span className="text-emerald-700 font-bold">Conforme: {p.conforme}</span>
                <span className="text-rose-700 font-bold">Déclassé: {p.declassee}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Last BL */}
      <div className="rounded-2xl bg-white border-2 border-slate-200 p-5">
        <h2 className="font-heading font-bold text-xl uppercase tracking-tight text-slate-900 mb-4 flex items-center gap-2">
          <Truck className="w-5 h-5" /> Dernier bon de livraison
        </h2>
        {data.dernier_bl ? (
          <button
            data-testid="dashboard-last-bl"
            onClick={() => navigate(`/bl/${data.dernier_bl.id}`)}
            className="w-full text-left rounded-xl border-2 border-slate-200 hover:border-slate-400 p-4 transition-colors flex items-center justify-between"
          >
            <div>
              <div className="font-mono font-black text-lg text-slate-900">{data.dernier_bl.numero}</div>
              <div className="text-sm text-slate-500 font-medium">
                {data.dernier_bl.destinataire.nom} • {fmtDate(data.dernier_bl.date_depart)} •{" "}
                {data.dernier_bl.nb_palettes} palettes
              </div>
            </div>
            <span className={`px-3 py-1 rounded-md font-bold text-xs uppercase ${
              data.dernier_bl.type === "P" ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"
            }`}>
              {data.dernier_bl.type === "P" ? "Conforme" : "Déclassé"}
            </span>
          </button>
        ) : (
          <p className="text-slate-400 font-medium">Aucun bon de livraison émis pour le moment.</p>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, tone, testId }) {
  const tones = {
    emerald: "text-emerald-600",
    rose: "text-rose-600",
    slate: "text-slate-700",
  };
  return (
    <div data-testid={testId} className="rounded-2xl bg-white border-2 border-slate-200 p-5">
      <Icon className={`w-6 h-6 mb-2 ${tones[tone]}`} />
      <div className="text-xs uppercase tracking-wider text-slate-400 font-bold">{label}</div>
      <div className="font-mono font-black text-4xl text-slate-900">{value}</div>
      <div className="text-xs text-slate-400 mt-1 font-semibold">{sub}</div>
    </div>
  );
}
